import React, { useCallback, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import { Button } from 'ksnm-common-ui/lib/Button';
import { Icon } from 'ksnm-common-ui/lib/Icon';
import { Select } from 'ksnm-common-ui/lib/Select';
import { sortBy, uniqBy } from 'lodash-es';

import { usePermission } from '@src/utils/hooks/usePermission';
import { fetchReferences } from '@stores/dictionaryStores/fetchReferences';
import { organizationsStore } from '@stores/dictionaryStores/organizationsStore';
import { reportSettingsStore } from '@stores/dictionaryStores/reportSettings/reportSettingsStore';

import styles from './Filter.module.scss';

export const FilterComponent = (): JSX.Element => {
  const { organizations } = organizationsStore;
  const {
    reportSettings,
    organizationCode,
    filters,
    setFilters,
    setOrganizationCode,
    clearOrganization,
    setSelectNode,
    setSelectedKeys,
  } = reportSettingsStore;

  const { reportGroup, reportType } = filters;

  const { VIEW: viewOrganization } = usePermission('CmOrganization');

  useEffect(() => {
    fetchReferences(['organizations']);
  }, []);

  useEffect(() => {
    const headOrganization = organizations.find((org) => org.head);

    if (!organizationCode && headOrganization && headOrganization.code) {
      setOrganizationCode(headOrganization.code);
    }
  }, [organizationCode, organizations, setOrganizationCode]);

  const onClear = useCallback(() => {
    setFilters({});
    clearOrganization();
    setSelectNode(null);
    setSelectedKeys([]);
  }, [clearOrganization, setFilters, setSelectNode, setSelectedKeys]);

  const onChange = useCallback(
    (value) => {
      if (!value) clearOrganization();
      setOrganizationCode(value);
      setSelectNode(null);
      setSelectedKeys([]);
    },
    [clearOrganization, setOrganizationCode, setSelectNode, setSelectedKeys],
  );

  const onChangeReportGroup = useCallback(
    (v) => {
      setFilters({ ...filters, reportGroup: v });
      setSelectNode(null);
      setSelectedKeys([]);
    },
    [filters, setFilters, setSelectNode, setSelectedKeys],
  );

  const onChangeReportType = useCallback(
    (v) => {
      setFilters({ ...filters, reportType: v });
      setSelectNode(null);
      setSelectedKeys([]);
    },
    [filters, setFilters, setSelectNode, setSelectedKeys],
  );

  const reportGroupItems = useMemo(
    () =>
      sortBy(
        uniqBy(
          reportSettings.filter((item) => {
            if (reportType && item.reportTypeCode !== reportType) {
              return false;
            }
            return !!item.reportGroupCode;
          }),
          'reportGroupCode',
        ).map((group) => ({
          id: group.reportGroupCode,
          name: `${group.reportGroupCode} - ${group.reportGroupName}`,
        })),
        'name',
      ),
    [reportType, reportSettings],
  );

  const reportTypeItems = useMemo(
    () =>
      sortBy(
        uniqBy(
          reportSettings.filter((item) => {
            if (reportGroup && item.reportGroupCode !== reportGroup) {
              return false;
            }
            return !!item.reportTypeCode;
          }),
          'reportTypeCode',
        ).map((group) => ({
          id: group.reportTypeCode,
          name: `${group.reportTypeCode} - ${group.reportTypeName}`,
        })),
        'name',
      ),
    [reportGroup, reportSettings],
  );

  return (
    <div className={styles.designFilters}>
      <div className={styles.items}>
        <Select
          items={organizations}
          placeholder="Выберите организацию"
          value={organizationCode}
          onChange={onChange}
          size="small"
          showSearch={false}
          allowClear
          className={styles.organizationSelect}
          disabled={!viewOrganization}
        />
        <Select
          items={reportGroupItems}
          placeholder="Группа отчета"
          value={reportGroup}
          onChange={onChangeReportGroup}
          size="small"
          showSearch
          allowClear
          className={styles.select}
        />
        <Select
          items={reportTypeItems}
          placeholder="Тип отчета"
          value={reportType}
          onChange={onChangeReportType}
          size="small"
          showSearch
          allowClear
          className={styles.select}
        />
      </div>

      <Button onClick={onClear} disabled={!organizationCode} icon={<Icon name="stop" />}>
        Сбросить фильтры
      </Button>
    </div>
  );
};

export const Filter = observer(FilterComponent);
